/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, April 17th 2022, 4:31:29 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import FormLabel from "@mui/material/FormLabel";
import GroupsIcon from "@mui/icons-material/Groups";
import UndoIcon from "@mui/icons-material/Undo";
import { withAccessCheck } from "authorization/hoc";
import {
  OSAPIForm,
  SwitchFormInput,
  ListButtonAutoCompleteFormInput,
  FormSubmitBubblingStopper,
} from "ui-widgets/forms";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { IconText, RadioFormInput, OSActionButton, StyledOSDialog } from "ui-widgets";
import {
  NotificationTemplate,
  SEND_WHEN_OPTIONS,
  SEND_WHEN_OPTIONS_DESC,
  TARGET_USERS,
  TARGET_USERS_DESC,
  NOTIFICATION_TYPES,
} from "lib/schemas";
import { useNotificationTemplateAPIForm, UseNotificationTemplateAPIFormSpec } from "service-builder/services";
import Grid from "@mui/material/Grid";
import { HTMLContentInput, TextContentInput } from "service-builder/components/layouts";
import NewTaskIcon from "@mui/icons-material/Pending";
import AssignedTaskIcon from "@mui/icons-material/AssignmentInd";
import CompletedTaskIcon from "@mui/icons-material/TaskAlt";
import SupervisedTaskIcon from "@mui/icons-material/SupervisedUserCircle";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import { TeamsGroupsTransferListInput } from "teams/components/TeamSelectorInputs";
import { TranslatableTextFormInput } from "translation/components";
import { AttachmentsInput } from "./AttachmentsInput";
import { MultipleRecipientSettingsInput } from "./MultipleRecipientSettingsInput";

const TARGET_USERS_ICONS: { [key in keyof typeof TARGET_USERS]: React.ReactNode } = {
  applicant: <PersonIcon color="secondary" />,
  staff: <GroupIcon color="secondary" />,
};

const SEND_WHEN_OPTIONS_ICONS: { [key in keyof typeof SEND_WHEN_OPTIONS]: React.ReactNode } = {
  task_assignment: <NewTaskIcon color="secondary" />,
  task_review: <AssignedTaskIcon color="secondary" />,
  task_supervise: <SupervisedTaskIcon color="secondary" />,
  task_done: <CompletedTaskIcon color="success" />,
  task_undone: <UndoIcon color="secondary" />,
};

interface NotificationTemplateFormFormProps extends UseNotificationTemplateAPIFormSpec {
  notificationIsForHumanTask?: boolean;
}

export const NotificationTemplateForm = ({
  notificationTemplateFetchId,
  notificationTemplateDefaultValues,
  updateNotificationTemplateFromAPICallback,
  onSuccessfulAPISave,
  notificationIsForHumanTask,
}: NotificationTemplateFormFormProps) => {
  const useAPIFormReturn = useNotificationTemplateAPIForm({
    notificationTemplateFetchId,
    notificationTemplateDefaultValues,
    updateNotificationTemplateFromAPICallback,
    onSuccessfulAPISave,
  });

  return (
    <FormSubmitBubblingStopper>
      <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton">
        <NotificationTemplateFields notificationIsForHumanTask={notificationIsForHumanTask} />
      </OSAPIForm>
    </FormSubmitBubblingStopper>
  );
};

/** Separating fields so we can consume the form context */

const NotificationTemplateFields = ({
  notificationIsForHumanTask,
}: Pick<NotificationTemplateFormFormProps, "notificationIsForHumanTask">) => {
  const { watch } = useOSFormContext<NotificationTemplate>();

  const [watchedEnabledValue, watchedTypeValue, watchedMultipleSendMethod, watchedMultipleCopyMethod] = watch<
    (keyof NotificationTemplate)[]
  >(["enabled", "type", "multiple_recipients_send_method", "multiple_recipients_copy_method"]) as [
    NotificationTemplate["enabled"],
    NotificationTemplate["type"],
    NotificationTemplate["multiple_recipients_send_method"],
    NotificationTemplate["multiple_recipients_copy_method"]
  ];

  const notificationIsEmail = watchedTypeValue === "email";

  return (
    <Grid container spacing={2}>
      <Grid item xs={9}>
        <TranslatableTextFormInput<NotificationTemplate> label="Subject" schema_field_key="title" />

        {notificationIsEmail ? (
          <>
            <HTMLContentInput<NotificationTemplate> schema_field_key="message" label="Email Body" minHeight="200px" />

            <FormLabel component="h4">Attachments</FormLabel>
            <AttachmentsInput />
          </>
        ) : (
          <TextContentInput<NotificationTemplate> label="SMS Template" schema_field_key="message" />
        )}
      </Grid>

      <Grid item xs={3}>
        <NotificationTypeSelector />

        <ListButtonAutoCompleteFormInput<NotificationTemplate>
          schema_field_key="target_users"
          label="Send To"
          options={Object.keys(TARGET_USERS)}
          getOptionLabel={(option) => TARGET_USERS[option]} // For the TextField
          getPrimaryOptionLabel={(option) => (
            <IconText icon={TARGET_USERS_ICONS[option]} textComponent={TARGET_USERS[option]} />
          )}
          getSecondaryOptionLabel={(option) => TARGET_USERS_DESC[option]}
        />

        {notificationIsForHumanTask ? (
          <ListButtonAutoCompleteFormInput<NotificationTemplate>
            schema_field_key="send_when"
            label="Send When"
            options={Object.keys(SEND_WHEN_OPTIONS)}
            getOptionLabel={(option) => SEND_WHEN_OPTIONS[option]} // For the TextField
            getPrimaryOptionLabel={(option) => (
              <IconText icon={SEND_WHEN_OPTIONS_ICONS[option]} textComponent={SEND_WHEN_OPTIONS[option]} />
            )}
            getSecondaryOptionLabel={(option) => SEND_WHEN_OPTIONS_DESC[option]}
          />
        ) : null}

        <SwitchFormInput<NotificationTemplate>
          schema_field_key="enabled"
          label={`${watchedEnabledValue ? "Disable" : "Enable"} Notification`}
        />

        {notificationIsEmail ? (
          <StyledOSDialog
            popUpIcon={<GroupsIcon color="primary" />}
            popUpTitle="Multiple Recipient Settings"
            renderOpenButton={({ handleOpen }) => (
              <OSActionButton actionStyle="custom" customIcon={<GroupsIcon />} onClick={handleOpen}>
                Multiple Recipient Settings
              </OSActionButton>
            )}
            renderContentWithClose={({ handleClose }) => (
              <MultipleRecipientSettingsInput
                multiple_recipients_send_method={watchedMultipleSendMethod}
                multiple_recipients_copy_method={watchedMultipleCopyMethod}
              />
            )}
          />
        ) : null}
      </Grid>
      <Grid item xs={12}>
        <TeamsGroupsTransferListInput<NotificationTemplate>
          schema_field_key="additional_recipients_groups"
          label="Teams for any Additional Recipients"
          helperText="Teams for recipients of this notification other than the submitter and users with permission to execute, assign or supervise the task. This setting is helpful when you want a group of users to receive a notification without them having to carry out any action on the task."
        />
      </Grid>
    </Grid>
  );
};

/* Disable SMS notification configuration for non-enterprise SaaS setups */
const NotificationTypeSelector = withAccessCheck(
  {
    resourceId: "configureSMSNotifications",
    invalidTitle: "Upgrade to enable SMS notifications",
    invalidMessage: <></>,
  },
  () => (
    <RadioFormInput<NotificationTemplate>
      schema_field_key="type"
      label="Notification Type"
      options={NOTIFICATION_TYPES}
    />
  ),
  ({ children }) => <div style={{ marginBottom: "20px" }}>{children}</div>
);
